"use client";
import SignIn from "@components/sign-in";
import Link from "next/link";
import Header from "@repo/ui/components/ui/header/index";
import { useTranslations } from "next-intl";
import { Session } from "next-auth";
import GradientButton from "@repo/ui/components/ui/gradient-button";
import Icon from "@repo/ui/components/ui/icon/icon";
import { Category } from "@/types/homepage";
import { useCategories } from "@/data/categories";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { Logo } from "@repo/ui/components/ui/header/logo";
import NavLogged from "@components/nav-logged";
import SavingsInfoBanner from "@components/savings-info-banner";
import { usePathname } from "next/navigation";
import { cn } from "@repo/ui/lib/utils";
import { useSession } from "next-auth/react";
import LanguageSwitcher from "@components/language-switcher";
import { useBrandMenu } from "@/data/brand";
import ShippingInfoBanner from "@components/shipping-info-banner";
import SignOut from "@repo/next-shared/components/sign-out";

type NavProps = {
  session: Session | null;
  children?: React.ReactNode;
};

const shouldShowSavingsInfo = (path: string) => {
  const exclude = ["make-an-offer", "checkout"];

  return !exclude.some((ex) => path.includes(ex));
};

const NavSkeleton = (targetHiddenMobile: boolean) => (
  <div>
    <div className="max-md:hidden px-10 h-[100px] flex gap-8 bg-primary-white dark:bg-primary-black items-center">
      <Logo />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1 dark:bg-secondary-gray1/20" />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1 dark:bg-secondary-gray1/20" />
      <span className="flex-1"></span>
      <Skeleton className="h-8 w-8 rounded-full my-4 bg-secondary-gray1 dark:bg-secondary-gray1/20" />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1 dark:bg-secondary-gray1/20" />
    </div>
    <div className="relative md:hidden px-4 h-[88px] justify-between flex gap-8 bg-primary-white dark:bg-primary-black items-center">
      <Skeleton className="h-8 w-8 rounded-full bg-secondary-gray1 dark:bg-secondary-gray1/20" />
      <Logo className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]" />
      <div className="flex gap-2">
        <Skeleton
          className={cn(
            "h-8 w-8 rounded-full bg-secondary-gray1 dark:bg-secondary-gray1/20",
            {
              "max-md:hidden": targetHiddenMobile,
            },
          )}
        />
        <Skeleton className="h-8 w-8 rounded-full bg-secondary-gray1 dark:bg-secondary-gray1/20" />
      </div>
    </div>
    <div className="flex gap-8 bg-primary-black dark:bg-primary-white justify-center max-md:hidden">
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1/20 dark:bg-secondary-gray1" />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1/20 dark:bg-secondary-gray1" />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1/20 dark:bg-secondary-gray1" />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1/20 dark:bg-secondary-gray1" />
      <Skeleton className="h-8 w-24 rounded-xl my-4 bg-secondary-gray1/20 dark:bg-secondary-gray1" />
    </div>
  </div>
);

export default function Nav({ session, children }: NavProps) {
  const { data: categories, isLoading } = useCategories();
  const t = useTranslations("Nav");
  const pathname = usePathname();
  const brands = useBrandMenu();
  const secondaryMenuItems = [...categories, brands];

  const targetHiddenMobile =
    pathname === "/" ||
    pathname.split("/").includes("search") ||
    pathname.split("/").includes("product") ||
    pathname.split("/").includes("make-an-offer") ||
    pathname.split("/").includes("checkout");

  const primaryItems: any[] = [
    { text: t("go_to_profile"), href: "/profile/overview" },
    { text: t("orders"), href: "/profile/orders" },
    { text: t("wishlist"), href: "/profile/wishlist" },
    { text: t("notifications"), href: "/notifications" },
  ];

  if (isLoading) {
    return NavSkeleton(targetHiddenMobile);
  }

  return (
    <>
      <Header
        primaryItems={primaryItems}
        secondaryItems={secondaryMenuItems as Category[]}
        LinkCmp={Link}
        primaryMenuHiddenOnDesktop={true}
        brandsLabel={t("main-brands")}
      >
        {{
          closed: session ? (
            <NavLogged
              session={session}
              children={children}
              targetHiddenMobile={targetHiddenMobile}
            />
          ) : (
            <div className="flex gap-[40px] max-md:gap-[12px] items-center">
              <Link
                className={cn({
                  "max-md:hidden": targetHiddenMobile,
                })}
                href="/search"
              >
                <Icon
                  icon="Search"
                  className="max-md:hidden h-[46px] w-[46px] flex items-center justify-center rounded-full bg-primary-black dark:bg-primary-white *:stroke-primary-white  dark:*:stroke-primary-magenta *:h-[22px] *:w-[22px]"
                />
                <Icon
                  icon="SearchCircle"
                  className="h-[24px] w-[24px] hidden max-md:block"
                />
              </Link>
              <SignIn />
            </div>
          ),
          open: (
            <Link
              className="fixed top-[12px] right-[-32px] h-[150px] w-[150px]"
              href="/search"
            >
              <GradientButton icon="Search" />
            </Link>
          ),
          languageSwitcher: <LanguageSwitcher />,
          logout: session && <div>{children}</div>,
        }}
      </Header>
      {session && shouldShowSavingsInfo(pathname) && <SavingsInfoBanner />}
      <ShippingInfoBanner />
    </>
  );
}
